import { partition } from 'lodash-es';
import { useMemo } from 'react';

import type { Formatter } from '../../core/types/formatter.js';
import {
  DEFAULT_MIN_NODE_SIZE,
  DEFAULT_MIN_NODE_SIZE_PARENT,
  DEFAULT_MIN_NODE_SIZE_PARENT_AND_LABELS,
} from '../constants.js';
import { useNameAccessor } from '../context/NameAccessorContext.js';
import { ClusterNode, RootNode } from '../types/guards/node-guards.js';
import { buildAggregatedLeaf } from '../utils/build-aggregated-leaf.js';
import { isBelowThreshold } from '../utils/is-below-threshold.js';
import { getAggregatedCluster } from '../utils/node-aggregations.js';

/**
 * Calculates the aggregated data for a treemap based on the provided data and treemap area.
 * @param data - TreeMap data
 * @param treemapArea - available area for the TreeMap
 * @param hasParentLabels - Flag for indicate if the cluster has the labels
 * @param formatter - formatter
 * @returns - TreeMap data with aggregated nodes for each cluster
 */

export const useAggregatedData = (
  data: RootNode,
  treemapArea: number,
  hasParentLabels: boolean,
  formatter: Formatter,
): { dataForRendering: RootNode; dataForHighlighting: RootNode } => {
  const nameAccessor = useNameAccessor();
  const areaPerValue = treemapArea / data.value;

  const [clustersToAggregate, remainingClusters] =
    data.children.length > 2
      ? partition(data.children, (node) =>
          isBelowThreshold(
            node,
            areaPerValue,
            hasParentLabels
              ? DEFAULT_MIN_NODE_SIZE_PARENT_AND_LABELS
              : DEFAULT_MIN_NODE_SIZE_PARENT,
          ),
        )
      : [[], data.children];

  const clustersWithAggregatedLeafs: ClusterNode[] = useMemo(
    () =>
      remainingClusters.map((node) => {
        return buildAggregatedLeaf(
          node as ClusterNode,
          areaPerValue,
          DEFAULT_MIN_NODE_SIZE,
          formatter,
          nameAccessor,
        );
      }),
    [remainingClusters, areaPerValue, formatter, nameAccessor],
  );

  const aggregatedClusters =
    clustersToAggregate.length > 0
      ? [getAggregatedCluster(clustersToAggregate as ClusterNode[], formatter)]
      : [];

  const dataForRendering = {
    ...data,
    children: [...clustersWithAggregatedLeafs, ...aggregatedClusters],
  };

  const dataForHighlighting = {
    ...data,
    children: [
      ...clustersWithAggregatedLeafs,
      ...clustersToAggregate,
      ...aggregatedClusters,
    ],
  };

  return { dataForRendering, dataForHighlighting };
};
